// ============================================================================
// TRAINING BOT SERVICE
// ============================================================================
// Provides AI opponents for training tables with three difficulty levels:
// - Easy: Plays loose, passive, makes many mistakes
// - Medium: Plays somewhat tight, semi-aggressive, occasional mistakes  
// - Hard: Plays GTO-style, tight-aggressive, minimal mistakes
//
// Bots make decisions based on position, hand strength, pot odds, and
// board texture - the same factors players should learn to consider.
// ============================================================================

import { Card, Player, GameState } from '../../types/GameState';
import {
  Position,
  PositionCategory,
  BoardTexture,
  HandStrength,
  DrawType,
  determinePosition,
  getPositionCategory,
  analyzeBoardTexture,
  evaluateHandStrength,
  detectDrawType,
  calculatePotOdds,
  estimateEquity,
  getOutsForDraw,
} from './trainingDecisionLogger';

// ============================================================================
// TYPES & INTERFACES
// ============================================================================

export type BotDifficulty = 'easy' | 'medium' | 'hard';

export interface BotPlayer {
  uid: string;
  username: string;
  displayName: string;
  difficulty: BotDifficulty;
  isBot: true;
  avatarUrl?: string;
  personality: BotPersonality;
}

export interface BotPersonality {
  vpipRange: [number, number]; // Min/max VPIP percentage
  aggressionFactor: number; // 0-3 (passive to aggressive)
  bluffFrequency: number; // 0-1
  foldToPressure: number; // 0-1 how often folds to aggression
  mistakeRate: number; // 0-1 how often makes suboptimal plays
  callStationFactor: number; // 0-1 how often calls instead of raising
}

export interface BotDecision {
  action: 'fold' | 'check' | 'call' | 'bet' | 'raise' | 'all-in';
  amount: number;
  reasoning?: string; // For debugging
}

// ============================================================================
// BOT NAMES
// ============================================================================

const BOT_NAMES: Record<BotDifficulty, string[]> = {
  easy: [
    'Mike_T', 'Sarah_92', 'JohnnyCards', 'Emma_Play',
    'Carlos_88', 'Lisa_Marie', 'Tommy_Chips', 'Rachel_K',
    'David_NYC', 'Jenny_Star', 'Mark_Vegas', 'Amy_Lu',
  ],
  medium: [
    'Alex_Pro', 'Samantha_J', 'Ryan_Ace', 'Olivia_Queen',
    'Brandon_99', 'Nicole_Win', 'Kevin_Stack', 'Megan_Rise',
    'Jason_Bet', 'Ashley_Gold', 'Chris_King', 'Laura_Luck',
  ],
  hard: [
    'Viktor_M', 'Natasha_X', 'Daniel_Elite', 'Sophia_Sharp',
    'Marcus_Edge', 'Elena_Pro', 'James_Shark', 'Victoria_Ace',
    'Andrew_Peak', 'Isabella_Top', 'Robert_Grind', 'Catherine_Win',
  ],
};

// ============================================================================
// BOT PERSONALITIES BY DIFFICULTY
// ============================================================================

const BOT_PERSONALITIES: Record<BotDifficulty, BotPersonality> = {
  easy: {
    vpipRange: [40, 60], // Plays way too many hands
    aggressionFactor: 0.5, // Very passive
    bluffFrequency: 0.1, // Rarely bluffs
    foldToPressure: 0.3, // Calls too much
    mistakeRate: 0.4, // Makes mistakes often
    callStationFactor: 0.7, // Calls instead of raises
  },
  medium: {
    vpipRange: [22, 32], // Reasonable range
    aggressionFactor: 1.2, // Slightly aggressive
    bluffFrequency: 0.2, // Occasional bluffs
    foldToPressure: 0.5, // Balanced folding
    mistakeRate: 0.15, // Occasional mistakes
    callStationFactor: 0.3, // Sometimes calls too much
  },
  hard: {
    vpipRange: [18, 25], // Tight
    aggressionFactor: 2.0, // Aggressive
    bluffFrequency: 0.25, // Well-balanced bluffs
    foldToPressure: 0.6, // Makes good folds
    mistakeRate: 0.05, // Rarely makes mistakes
    callStationFactor: 0.1, // Mostly raises or folds
  },
};

// ============================================================================
// HELPER FUNCTIONS
// ============================================================================

/**
 * Generate a random number between min and max
 */
function randomBetween(min: number, max: number): number {
  return Math.random() * (max - min) + min;
}

/**
 * Random boolean with probability
 */
function randomChance(probability: number): boolean {
  return Math.random() < probability;
}

/**
 * Get a random element from array
 */
function randomElement<T>(arr: T[]): T {
  return arr[Math.floor(Math.random() * arr.length)];
}

/**
 * Calculate bet size based on pot and strategy
 */
function calculateBetSize(
  potSize: number,
  minBet: number,
  maxBet: number,
  style: 'small' | 'medium' | 'large' | 'overbet'
): number {
  let multiplier: number;
  
  switch (style) {
    case 'small':
      multiplier = randomBetween(0.33, 0.5);
      break;
    case 'medium':
      multiplier = randomBetween(0.5, 0.75);
      break;
    case 'large':
      multiplier = randomBetween(0.75, 1.0);
      break;
    case 'overbet':
      multiplier = randomBetween(1.0, 1.5);
      break;
  }
  
  const betAmount = Math.round(potSize * multiplier);
  return Math.max(minBet, Math.min(betAmount, maxBet));
}

// ============================================================================
// BOT CREATION
// ============================================================================

let botCounter = 0;

/**
 * Create a bot player
 */
export function createBot(difficulty: BotDifficulty): BotPlayer {
  const names = BOT_NAMES[difficulty];
  const name = names[botCounter % names.length];
  botCounter++;
  
  const uid = `bot_${difficulty}_${Date.now()}_${Math.random().toString(36).substr(2, 6)}`;
  
  return {
    uid,
    username: name,
    displayName: name,
    difficulty,
    isBot: true,
    personality: { ...BOT_PERSONALITIES[difficulty] },
  };
}

/**
 * Create multiple bots with specified difficulties
 */
export function createBots(count: number, difficulty: BotDifficulty | 'mixed'): BotPlayer[] {
  const bots: BotPlayer[] = [];
  
  for (let i = 0; i < count; i++) {
    let botDifficulty: BotDifficulty;
    
    if (difficulty === 'mixed') {
      // Distribute evenly
      const difficulties: BotDifficulty[] = ['easy', 'medium', 'hard'];
      botDifficulty = difficulties[i % 3];
    } else {
      botDifficulty = difficulty;
    }
    
    bots.push(createBot(botDifficulty));
  }
  
  return bots;
}

// ============================================================================
// BOT DECISION MAKING
// ============================================================================

/**
 * Make a decision for a bot player
 */
export function makeBotDecision(params: {
  bot: BotPlayer;
  player: Player;
  gameState: GameState;
  seatIndex: number;
  dealerIndex: number;
  minBet: number;
  currentBet: number;
  potSize: number;
}): BotDecision {
  const { bot, player, gameState, seatIndex, dealerIndex, minBet, currentBet, potSize } = params;
  
  // Get personality - use defaults if not present (can happen after Firestore serialization)
  const personality = bot.personality || BOT_PERSONALITIES[bot.difficulty] || BOT_PERSONALITIES.medium;
  
  // Get position info
  const position = determinePosition(seatIndex, dealerIndex, gameState.players.length);
  const positionCategory = getPositionCategory(position);
  
  // Get hand info
  const holeCards = player.holeCards || [];
  const communityCards = gameState.communityCards || [];
  const { strength: handStrength } = evaluateHandStrength(holeCards, communityCards);
  const boardTexture = analyzeBoardTexture(communityCards);
  const drawType = detectDrawType(holeCards, communityCards);
  const equity = estimateEquity(drawType, handStrength, 
    communityCards.length === 0 ? 'preflop' : 
    communityCards.length === 3 ? 'flop' : 
    communityCards.length === 4 ? 'turn' : 'river'
  );
  
  // Calculate amounts
  const callAmount = currentBet - player.currentBet;
  const canCheck = callAmount === 0;
  const maxBet = player.chips;
  const potOdds = calculatePotOdds(potSize, callAmount);
  
  // Apply mistake rate - sometimes make suboptimal plays
  if (randomChance(personality.mistakeRate)) {
    return makeMistake(bot, canCheck, callAmount, minBet, maxBet, potSize);
  }
  
  // Preflop decision
  if (communityCards.length === 0) {
    return makePreflopDecision({
      bot,
      handStrength,
      positionCategory,
      callAmount,
      canCheck,
      minBet,
      maxBet,
      potSize,
    });
  }
  
  // Postflop decision
  return makePostflopDecision({
    bot,
    handStrength,
    drawType,
    equity,
    boardTexture,
    positionCategory,
    callAmount,
    canCheck,
    minBet,
    maxBet,
    potSize,
    potOdds,
  });
}

/**
 * Make a deliberate mistake (for easy bots)
 */
function makeMistake(
  bot: BotPlayer,
  canCheck: boolean,
  callAmount: number,
  minBet: number,
  maxBet: number,
  potSize: number
): BotDecision {
  const mistakes = [
    // Call with nothing
    { action: 'call' as const, amount: callAmount, reasoning: 'Mistake: calling with nothing' },
    // Min bet when should bet bigger
    { action: 'bet' as const, amount: minBet, reasoning: 'Mistake: weak bet' },
    // Fold when should call
    { action: 'fold' as const, amount: 0, reasoning: 'Mistake: folding too much' },
  ];

  const mistake = randomElement(mistakes);

  // REALISM CAP: mistakes are loose-passive leaks, not stack punts. A real
  // bad player over-calls SMALL bets - nobody calls an all-in with 7-2.
  // The calling mistake is only allowed when it costs <= 15% of the stack.
  if (mistake.action === 'call' && callAmount > 0 && callAmount <= maxBet
      && callAmount <= maxBet * 0.15) {
    return mistake;
  }
  if (mistake.action === 'bet' && canCheck) {
    return { ...mistake, amount: minBet };
  }
  if (mistake.action === 'fold' && !canCheck) {
    return mistake;
  }
  
  // Default: check when free. When facing a bet, the SAME realism cap
  // applies - a blocked call-mistake must not fall through into calling a
  // jam anyway (that fallback was exactly how bots called all-ins with
  // 4-9 offsuit). Cheap price: the loose call stands. Big price: fold.
  if (canCheck) return { action: 'check', amount: 0 };
  if (callAmount <= maxBet * 0.15) return { action: 'call', amount: callAmount };
  return { action: 'fold', amount: 0, reasoning: 'Mistake impulse, but not for this price' };
}

/**
 * Make preflop decision
 */
function makePreflopDecision(params: {
  bot: BotPlayer;
  handStrength: HandStrength;
  positionCategory: PositionCategory;
  callAmount: number;
  canCheck: boolean;
  minBet: number;
  maxBet: number;
  potSize: number;
}): BotDecision {
  const { bot, handStrength, positionCategory, callAmount, canCheck, minBet, maxBet, potSize } = params;
  const personality = bot.personality || BOT_PERSONALITIES[bot.difficulty] || BOT_PERSONALITIES.medium;
  
  // Determine if we should play this hand based on VPIP range
  const targetVPIP = randomBetween(personality.vpipRange[0], personality.vpipRange[1]) / 100;
  
  // Adjust play probability based on hand strength and position
  let playProbability: number;
  
  switch (handStrength) {
    case 'monster':
      playProbability = 1.0; // Always play
      break;
    case 'strong':
      playProbability = 0.95;
      break;
    case 'medium':
      playProbability = positionCategory === 'LP' ? 0.8 : 0.5;
      break;
    case 'weak':
      playProbability = positionCategory === 'LP' ? 0.4 : 0.15;
      break;
    case 'draw':
      playProbability = positionCategory === 'LP' ? 0.5 : 0.2;
      break;
    default:
      playProbability = targetVPIP * 0.5;
  }
  
  // Tighten up for hard bots in early position
  if (bot.difficulty === 'hard' && positionCategory === 'EP') {
    playProbability *= 0.6;
  }
  
  // Loosen up for easy bots
  if (bot.difficulty === 'easy') {
    playProbability = Math.min(1, playProbability * 1.5);
  }
  
  // Decision to play or fold
  if (!randomChance(playProbability) && !canCheck) {
    return { action: 'fold', amount: 0, reasoning: 'Hand not strong enough' };
  }
  
  // If we're playing, decide between call and raise
  const raiseChance = personality.aggressionFactor / 3;
  const shouldRaise = randomChance(raiseChance) && !randomChance(personality.callStationFactor);
  
  // Strong hands should usually raise
  if (handStrength === 'monster' || handStrength === 'strong') {
    const raiseSize = calculateBetSize(potSize, minBet * 3, maxBet, 'medium');
    return { 
      action: 'raise', 
      amount: raiseSize,
      reasoning: 'Raising for value with strong hand',
    };
  }
  
  if (shouldRaise && canCheck) {
    const raiseSize = calculateBetSize(potSize, minBet * 2.5, maxBet, 'small');
    return { action: 'bet', amount: raiseSize, reasoning: 'Opening raise' };
  }
  
  if (shouldRaise && callAmount > 0 && callAmount * 3 <= maxBet) {
    return { 
      action: 'raise', 
      amount: Math.min(callAmount * 3, maxBet),
      reasoning: '3-bet',
    };
  }
  
  // Call or check
  if (canCheck) {
    return { action: 'check', amount: 0 };
  }

  // BET-SIZE SANITY (the '72o calls an all-in' fix): deciding to *play* a
  // hand is not deciding to play it FOR ANY PRICE. Cap how much of the
  // stack a hand class will call preflop - weak hands call small raises,
  // only real hands continue against big raises and jams.
  if (callAmount > 0) {
    const stackAll = maxBet + callAmount; // chips before this call
    const costRatio = callAmount / Math.max(1, stackAll);
    let cap: number;
    switch (handStrength) {
      case 'monster': cap = 1.0; break;                                   // never folds preflop
      case 'strong':  cap = bot.difficulty === 'hard' ? 0.6 : 1.0; break; // hard bots avoid coolers with AQ/JJ-type hands
      case 'medium':  cap = 0.35; break;                                  // calls opens, folds to jams
      case 'draw':    cap = 0.15; break;                                  // suited junk peels cheap
      default:        cap = 0.10; break;                                  // weak trash only ever calls tiny raises
    }
    // Easy bots are looser (that's their identity) but still not suicidal.
    if (bot.difficulty === 'easy') cap = Math.min(1, cap * 1.6);
    if (costRatio > cap) {
      return { action: 'fold', amount: 0, reasoning: `Fold: ${handStrength} hand vs bet costing ${Math.round(costRatio * 100)}% of stack` };
    }
  }

  if (callAmount <= maxBet) {
    return { action: 'call', amount: callAmount };
  }

  return { action: 'fold', amount: 0 };
}

/**
 * Make postflop decision
 */
function makePostflopDecision(params: {
  bot: BotPlayer;
  handStrength: HandStrength;
  drawType: DrawType;
  equity: number;
  boardTexture: BoardTexture;
  positionCategory: PositionCategory;
  callAmount: number;
  canCheck: boolean;
  minBet: number;
  maxBet: number;
  potSize: number;
  potOdds: number;
}): BotDecision {
  const {
    bot, handStrength, drawType, equity, boardTexture, positionCategory,
    callAmount, canCheck, minBet, maxBet, potSize, potOdds
  } = params;
  const personality = bot.personality || BOT_PERSONALITIES[bot.difficulty] || BOT_PERSONALITIES.medium;
  
  // Value betting with strong hands
  if (handStrength === 'monster' || handStrength === 'strong') {
    if (canCheck && randomChance(0.1)) {
      // Occasional trap
      return { action: 'check', amount: 0, reasoning: 'Trapping with strong hand' };
    }
    
    const betStyle = handStrength === 'monster' ? 'large' : 'medium';
    const betSize = calculateBetSize(potSize, minBet, maxBet, betStyle);
    
    if (canCheck) {
      return { action: 'bet', amount: betSize, reasoning: 'Value betting' };
    }
    
    if (callAmount > 0) {
      // Facing a bet with strong hand
      const raiseChance = personality.aggressionFactor / 2;
      if (randomChance(raiseChance) && callAmount * 2 <= maxBet) {
        return { 
          action: 'raise', 
          amount: Math.min(callAmount * 2.5, maxBet),
          reasoning: 'Raising for value',
        };
      }
      return { action: 'call', amount: callAmount };
    }
    
    return { action: 'bet', amount: betSize };
  }
  
  // Drawing hands
  if (drawType !== 'none' && handStrength !== 'strong' && handStrength !== 'monster') {
    const outs = getOutsForDraw(drawType);
    const requiredEquity = callAmount / (potSize + callAmount) * 100;
    
    // Check pot odds
    if (callAmount > 0) {
      if (equity >= requiredEquity) {
        // Profitable call
        return { action: 'call', amount: callAmount, reasoning: 'Calling with good pot odds' };
      }
      
      // Semi-bluff raise opportunity
      if (personality.aggressionFactor > 1 && 
          drawType !== 'gutshot' && 
          randomChance(personality.bluffFrequency) &&
          callAmount * 2 <= maxBet) {
        return { 
          action: 'raise', 
          amount: Math.min(callAmount * 2.5, maxBet),
          reasoning: 'Semi-bluff with draw',
        };
      }
      
      // Fold if pot odds are bad and we're not making mistakes
      if (equity < requiredEquity - 10) {
        return { action: 'fold', amount: 0, reasoning: 'Folding draw with bad odds' };
      }
      
      // Call anyway if call station
      if (randomChance(personality.callStationFactor)) {
        return { action: 'call', amount: callAmount, reasoning: 'Calling - call station' };
      }
      
      return { action: 'fold', amount: 0 };
    }
    
    // Can check - maybe semi-bluff bet
    if (canCheck && randomChance(personality.bluffFrequency) && drawType !== 'gutshot') {
      const betSize = calculateBetSize(potSize, minBet, maxBet, 'small');
      return { action: 'bet', amount: betSize, reasoning: 'Semi-bluff bet' };
    }
    
    return { action: 'check', amount: 0 };
  }
  
  // Medium strength hands - pot control
  if (handStrength === 'medium') {
    if (canCheck) {
      // Check for pot control most of the time
      if (randomChance(0.7)) {
        return { action: 'check', amount: 0, reasoning: 'Pot control' };
      }
      // Thin value bet sometimes
      const betSize = calculateBetSize(potSize, minBet, maxBet, 'small');
      return { action: 'bet', amount: betSize, reasoning: 'Thin value bet' };
    }
    
    // Facing bet with medium hand
    if (callAmount > 0) {
      // Fold to large bets
      if (callAmount > potSize * 0.75) {
        if (randomChance(personality.foldToPressure)) {
          return { action: 'fold', amount: 0, reasoning: 'Folding to pressure' };
        }
      }
      
      // Call smaller bets
      if (callAmount <= potSize * 0.5) {
        return { action: 'call', amount: callAmount, reasoning: 'Calling small bet' };
      }
      
      // Medium bets - depends on personality
      if (randomChance(personality.callStationFactor)) {
        return { action: 'call', amount: callAmount };
      }
      
      return { action: 'fold', amount: 0 };
    }
  }
  
  // Weak hands / nothing
  if (handStrength === 'weak' || handStrength === 'nothing') {
    // Bluffing opportunity
    if (canCheck && randomChance(personality.bluffFrequency)) {
      // Better spots: dry board, in position, heads-up
      const goodBluffSpot = boardTexture === 'dry' && positionCategory === 'LP';
      
      if (goodBluffSpot || bot.difficulty === 'easy') {
        const betSize = calculateBetSize(potSize, minBet, maxBet, 'medium');
        return { action: 'bet', amount: betSize, reasoning: 'Bluffing' };
      }
    }
    
    if (canCheck) {
      return { action: 'check', amount: 0 };
    }
    
    // Facing bet with nothing
    if (callAmount > 0) {
      // Maybe bluff raise (hard bots only)
      if (bot.difficulty === 'hard' && 
          randomChance(personality.bluffFrequency * 0.5) &&
          boardTexture === 'dry' &&
          callAmount * 2 <= maxBet) {
        return { 
          action: 'raise', 
          amount: Math.min(callAmount * 2.5, maxBet),
          reasoning: 'Bluff raise',
        };
      }
      
      // Usually fold
      return { action: 'fold', amount: 0, reasoning: 'Folding weak hand' };
    }
  }
  
  // Default
  if (canCheck) {
    return { action: 'check', amount: 0 };
  }
  return { action: 'fold', amount: 0 };
}

/**
 * Add thinking delay for more realistic bot behavior
 */
export function getBotThinkingDelay(difficulty: BotDifficulty): number {
  const baseDelay = {
    easy: 800,
    medium: 1200,
    hard: 1500,
  };
  
  // Add some randomness
  const variance = baseDelay[difficulty] * 0.5;
  return baseDelay[difficulty] + (Math.random() - 0.5) * variance;
}

// ============================================================================
// EXPORT
// ============================================================================

export default {
  createBot,
  createBots,
  makeBotDecision,
  getBotThinkingDelay,
};